import {
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/joy";
import type { JobProfit } from "../../types/profitDesk";

const money = (value: string | number) =>
  Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" });

export function JobFinancialOverview({
  data,
  editing,
  canEdit,
  onEdit,
}: {
  data: JobProfit;
  editing: boolean;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const contract = Number(data.contract_value);
  const collected = Number(data.collected);
  const balance = Number(data.contract_balance);
  const progress =
    contract > 0 ? Math.max(0, Math.min(100, (collected / contract) * 100)) : 0;
  const loss = Number(data.projected_contribution) < 0;
  const remainingCosts =
    Number(data.committed_costs) + Number(data.additional_remaining);
  const commissionMissing = data.warnings.some((message) =>
    message.includes("commission has not been accrued"),
  );
  const coverageMissing = data.warnings.some((message) =>
    message.startsWith("Cost coverage has not been reviewed"),
  );
  const margin =
    data.projected_margin === null
      ? null
      : Number(data.projected_margin).toLocaleString("en-US", {
          maximumFractionDigits: 1,
        });
  const row = (label: string, amount: string, description: string) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        py: 1.25,
      }}
    >
      <Box>
        <Typography level="title-sm">{label}</Typography>
        <Typography level="body-xs">{description}</Typography>
      </Box>
      <Typography
        level="title-md"
        sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
      >
        {money(amount)}
      </Typography>
    </Box>
  );

  return (
    <Stack gap={2.5}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Box>
          <Typography level="h3">Financial overview</Typography>
          <Typography level="body-sm" sx={{ mt: 0.5 }}>
            See where the job stands, from contract to company profit.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="neutral"
          disabled={!canEdit}
          onClick={onEdit}
        >
          {editing ? "Close cost plan" : "Review cost plan"}
        </Button>
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 1.7fr) minmax(0, 1fr)",
          },
          gap: 2,
        }}
      >
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "lg",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              flexWrap="wrap"
              alignItems="center"
              gap={1}
              sx={{ mb: 2.5 }}
            >
              <Typography level="title-lg">Job profitability</Typography>
              <Chip size="sm" variant="soft">
                Estimate at completion
              </Chip>
            </Stack>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr auto 1fr" },
                gap: { xs: 1, sm: 1.5 },
                alignItems: "center",
              }}
            >
              <Box>
                <Typography level="body-sm">Contract total</Typography>
                <Typography
                  level="h3"
                  sx={{ my: 0.5, fontVariantNumeric: "tabular-nums" }}
                >
                  {money(data.contract_value)}
                </Typography>
                <Typography level="body-xs">Agreed job value</Typography>
              </Box>
              <Typography
                aria-hidden
                level="h3"
                sx={{
                  color: "text.tertiary",
                  display: { xs: "none", sm: "block" },
                }}
              >
                −
              </Typography>
              <Box>
                <Typography level="body-sm">Expected job costs</Typography>
                <Typography
                  level="h3"
                  sx={{ my: 0.5, fontVariantNumeric: "tabular-nums" }}
                >
                  {money(data.projected_costs)}
                </Typography>
                <Typography level="body-xs">
                  Recorded + committed + remaining
                </Typography>
              </Box>
              <Typography
                aria-hidden
                level="h3"
                sx={{
                  color: "text.tertiary",
                  display: { xs: "none", sm: "block" },
                }}
              >
                =
              </Typography>
              <Box>
                <Typography level="body-sm">Gross profit</Typography>
                <Typography
                  level="h3"
                  sx={{ my: 0.5, fontVariantNumeric: "tabular-nums" }}
                >
                  {money(data.projected_gross_profit)}
                </Typography>
                <Typography level="body-xs">
                  Before overhead & commission
                </Typography>
              </Box>
            </Box>
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              sx={{ mt: 2.5 }}
            >
              {margin !== null ? (
                <Chip
                  size="sm"
                  color={data.below_floor ? "danger" : "neutral"}
                  variant="soft"
                >
                  {margin}% gross margin
                  {data.below_floor ? " · Below target" : ""}
                </Chip>
              ) : (
                <Chip size="sm">Margin unavailable</Chip>
              )}
              <Typography level="body-xs">
                Target: {Number(data.margin_floor)}% or higher
              </Typography>
            </Stack>
          </Box>
          <Box
            sx={{
              px: { xs: 2, md: 2.5 },
              bgcolor: "background.level1",
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            {row(
              "Overhead allowance",
              data.overhead_allowance,
              "Operating costs allocated in the cost plan",
            )}
            <Divider />
            {row(
              "Recorded commission",
              data.recorded_commissions,
              commissionMissing
                ? "Not yet included — see the commission estimate below"
                : "Accrued commission, including approved adjustments",
            )}
          </Box>
          <Box
            sx={{
              p: { xs: 2, md: 2.5 },
              bgcolor: loss ? "danger.softBg" : "success.softBg",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography level="title-md">
                {loss ? "Projected company loss" : "Projected company profit"}
              </Typography>
              <Typography level="body-xs">
                Gross profit − overhead − recorded commission
              </Typography>
              {commissionMissing && (
                <Typography level="body-xs" color="warning" sx={{ mt: 0.5 }}>
                  Will decrease when commission is recorded.
                </Typography>
              )}
            </Box>
            <Typography
              level="h2"
              color={loss ? "danger" : "success"}
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {money(data.projected_contribution)}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "lg",
            p: { xs: 2, md: 2.5 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography level="title-lg">Customer payments</Typography>
          <Typography level="body-sm">
            Track money received separately from job profit.
          </Typography>
          <Box>
            <Typography level="body-sm">Paid by customer</Typography>
            <Typography
              level="h2"
              sx={{ mt: 0.5, fontVariantNumeric: "tabular-nums" }}
            >
              {money(data.collected)}
            </Typography>
            <Typography level="body-xs">After any refunds</Typography>
          </Box>
          <Box>
            <LinearProgress
              determinate
              value={progress}
              aria-label="Contract payment progress"
              color="primary"
              sx={{ "--LinearProgress-thickness": "8px", borderRadius: "sm" }}
            />
            <Typography level="body-xs" sx={{ mt: 1 }}>
              {contract > 0
                ? `${Number(progress.toFixed(1))}% of the contract collected`
                : "Set a contract value to track payment progress"}
            </Typography>
          </Box>
          <Divider />
          <Box
            sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
          >
            <Typography level="title-sm">
              {balance < 0 ? "Customer overpayment" : "Still to collect"}
            </Typography>
            <Typography level="title-lg">{money(Math.abs(balance))}</Typography>
          </Box>
          <Box
            sx={{
              bgcolor: "background.level1",
              borderRadius: "md",
              p: 1.5,
              mt: "auto",
            }}
          >
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Typography level="body-sm">Unpaid invoices</Typography>
              <Typography level="title-sm">
                {money(data.issued_receivables)}
              </Typography>
            </Stack>
            <Typography level="body-xs" sx={{ mt: 0.75 }}>
              Only invoices already issued. The remaining contract balance may
              include work you haven’t billed yet.
            </Typography>
          </Box>
        </Box>
      </Box>
      {data.warnings.length > 0 && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "warning.outlinedBorder",
            bgcolor: "warning.softBg",
            borderRadius: "md",
            p: 2,
          }}
        >
          <Typography level="title-sm" sx={{ mb: 0.5 }}>
            This estimate needs a review
          </Typography>
          <Typography level="body-sm">
            {coverageMissing
              ? "Confirm all job costs are accounted for before relying on the profit estimate."
              : "Review the items below before relying on the profit estimate."}
            {remainingCosts > 0
              ? ` ${money(remainingCosts)} of expected costs are still to be recorded.`
              : ""}
          </Typography>
          <Box component="details" sx={{ mt: 1 }}>
            <Typography
              component="summary"
              level="title-sm"
              sx={{
                cursor: "pointer",
                display: "list-item",
                listStylePosition: "inside",
                width: "fit-content",
                "&:focus-visible": { outline: "2px solid", outlineOffset: 3 },
              }}
            >
              {data.warnings.length}{" "}
              {data.warnings.length === 1 ? "item" : "items"} to review
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, mb: 0 }}>
              {data.warnings.map((message) => (
                <Typography
                  component="li"
                  level="body-sm"
                  key={message}
                  sx={{ mt: 0.75 }}
                >
                  {message}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Stack>
  );
}
